/**
 * Prisma-owned primitives (ADR 0003 rule 6; ADR 0016). No user-facing strings live here:
 * every label arrives as a prop already translated by the app's i18n catalog.
 * Colours come only from design tokens (no hex values in components).
 */
import { STATUS_TONE_VARS, type StatusTone } from "@prisma/design-tokens";
import type { ButtonHTMLAttributes, CSSProperties, HTMLAttributes, ReactNode } from "react";

export type { StatusTone };

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** Status chip (decision O7): always icon + label, never colour alone. */
export function StatusChip({
  tone,
  icon,
  label,
  className,
}: {
  tone: StatusTone;
  icon: ReactNode;
  label: string;
  className?: string;
}) {
  const v = STATUS_TONE_VARS[tone];
  const style: CSSProperties = { color: v.fg, background: v.bg };
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide",
        className,
      )}
      style={style}
      data-tone={tone}
    >
      <span aria-hidden="true" className="inline-flex size-3.5 items-center justify-center">
        {icon}
      </span>
      {label}
    </span>
  );
}

type ButtonVariant = "primary" | "secondary" | "ghost";

export function Button({
  variant = "secondary",
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-[var(--radius-control)] px-4 h-10 text-sm font-medium transition-[background,opacity,box-shadow] duration-150 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]";
  const variants: Record<ButtonVariant, string> = {
    primary:
      "text-[var(--text-on-accent)] bg-[image:var(--gradient-primary-button)] hover:opacity-95 shadow-[0_4px_18px_var(--accent-primary-soft)]",
    secondary:
      "text-[var(--text-primary)] bg-[var(--bg-surface)] border border-[var(--border-default)] hover:bg-[var(--bg-elevated)]",
    ghost: "text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]",
  };
  return (
    <button type="button" className={cx(base, variants[variant], className)} {...rest}>
      {children}
    </button>
  );
}

export function Card({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cx(
        "rounded-[var(--radius-card)] border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-[var(--shadow-card)]",
        className,
      )}
      {...rest}
    />
  );
}

/** Tinted icon tile, as on the Dashboard attention tiles in the locked screens. */
export function IconTile({ tone, children }: { tone: StatusTone; children: ReactNode }) {
  const v = STATUS_TONE_VARS[tone];
  return (
    <span
      aria-hidden="true"
      className="inline-flex size-12 shrink-0 items-center justify-center rounded-[var(--radius-card)]"
      style={{ color: v.fg, background: v.bg }}
    >
      {children}
    </span>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-6 min-w-6 items-center justify-center rounded-md border border-[var(--border-default)] bg-[var(--bg-elevated)] px-1.5 font-[family-name:var(--font-sans)] text-[11px] text-[var(--text-secondary)]">
      {children}
    </kbd>
  );
}
